import React, { useCallback, useEffect, useState } from "react";
import { Search, RefreshCw, Download, Phone, Copy, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../components/AdminLayout";
import { api } from "../lib/api";

const EVENT_FILTERS = [
  { key: "all", label: "All" },
  { key: "page_view", label: "Page views" },
  { key: "add_to_plan", label: "Add to plan" },
  { key: "checkout_start", label: "Checkout" },
  { key: "payment_success", label: "Paid" },
  { key: "payment_failed", label: "Failed pay" },
];

export default function AdminActivityPage() {
  const [q, setQ] = useState("");
  const [eventType, setEventType] = useState("all");
  const [data, setData] = useState({ events: [], leads: [] });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (q.trim()) params.q = q.trim();
      if (eventType !== "all") params.event_type = eventType;
      const res = await api.get("/admin/events", { params });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load activity");
    } finally {
      setLoading(false);
    }
  }, [q, eventType]);

  useEffect(() => {
    load();
  }, [load]);

  const exportCsv = () => {
    if (!data.leads.length) return toast("Nothing to export.");
    const rows = [
      ["Email", "Phone", "Last URL", "Last Path", "Last Sub Code", "Last Event", "Events", "Sub Codes Viewed", "Last Seen"],
      ...data.leads.map((l) => [
        l.email,
        l.phone,
        l.last_url,
        l.last_path,
        l.last_sub_code || "",
        l.last_event_type || "",
        String(l.event_count),
        (l.sub_codes || []).join(" | "),
        l.last_seen,
      ]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `authoros-activity-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${data.leads.length} leads.`);
  };

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">Studio admin</div>
          <h1 className="mt-2 font-serif-display text-[34px] font-medium leading-[0.98] tracking-tight sm:text-[42px] lg:text-[46px]">
            Lead activity<span className="text-brand">.</span>
          </h1>
          <p className="mt-2 max-w-[560px] text-[13px] text-brand-ivory/60">
            Live feed of which authors are viewing which service pages. Call the hottest leads first.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={load}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-brand-hair text-brand-ivory hover:border-brand hover:text-brand"
            aria-label="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={exportCsv}
            className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2.5 font-mono text-[10.5px] uppercase tracking-[0.28em] text-white hover:bg-[#ff5c26]"
          >
            <Download className="h-4 w-4" /> Export Leads
          </button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto]">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-mute" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by email, phone, path or sub-code (e.g. 02.4)"
            className="h-11 w-full rounded-md border border-brand-hair bg-brand-panel px-10 text-[13px] text-brand-ivory placeholder:text-brand-mute focus:border-brand"
          />
        </div>
        <div className="flex overflow-hidden rounded-md border border-brand-hair bg-brand-panel">
          {EVENT_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setEventType(f.key)}
              className={`px-3 py-2.5 font-mono text-[10.5px] uppercase tracking-[0.22em] ${
                eventType === f.key ? "bg-brand text-white" : "text-brand-ivory/70 hover:bg-white/[0.04]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Hot leads */}
      <div className="mt-8">
        <div className="mb-3 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
          Hot leads &mdash; {data.leads.length}
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {data.leads.length === 0 && (
            <div className="rounded-xl border border-brand-hair bg-brand-panel px-6 py-8 text-center font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute md:col-span-2">
              {loading ? "Loading..." : "No activity yet. Ask an author to browse the studio."}
            </div>
          )}
          {data.leads.map((l) => (
            <LeadCard key={l.email} lead={l} />
          ))}
        </div>
      </div>

      {/* Raw event feed */}
      <div className="mt-10">
        <div className="mb-3 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
          Recent events &mdash; {data.events.length}
        </div>
        <div className="overflow-hidden rounded-xl border border-brand-hair bg-brand-panel">
          <div className="overflow-x-auto">
            <div className="min-w-[720px]">
              <div className="grid grid-cols-[160px_1fr_120px_160px] gap-4 border-b border-brand-hair px-6 py-3 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                <div>Email</div>
                <div>Path</div>
                <div>Type</div>
                <div>When</div>
              </div>
              {data.events.slice(0, 100).map((e) => (
                <div key={e.id} className="grid grid-cols-[160px_1fr_120px_160px] items-center gap-4 border-b border-brand-hair/60 px-6 py-2.5 text-[12.5px]">
                  <div className="truncate text-brand-ivory/85">{e.email || "(anon)"}</div>
                  <div className="min-w-0 truncate">
                    <span className="text-brand-ivory">{e.path}</span>
                    {e.sub_code && <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.22em] text-brand">{e.sub_code}</span>}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-mute">{e.event_type}</div>
                  <div className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-brand-mute">
                    {e.created_at ? new Date(e.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function LeadCard({ lead }) {
  const copy = async (val) => {
    try { await navigator.clipboard.writeText(val); toast.success("Copied"); } catch (_e) { /* noop */ }
  };
  const isHot = lead.event_count >= 5 || ["add_to_plan", "checkout_start"].includes(lead.last_event_type);
  return (
    <div className={`rounded-xl border p-5 ${isHot ? "border-brand/60 bg-brand/[0.06]" : "border-brand-hair bg-brand-panel"}`}>
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-brand-mute">
            <span>{lead.event_count} events</span>
            {isHot && <span className="rounded bg-brand px-2 py-0.5 text-white">Hot</span>}
          </div>
          <div className="mt-1 font-serif-display text-[18px] leading-tight text-brand-ivory">
            {lead.email}
          </div>
          {lead.phone && (
            <a
              href={`tel:${lead.phone}`}
              className="mt-1 inline-flex items-center gap-1.5 font-mono text-[11.5px] text-brand hover:underline"
            >
              <Phone className="h-3 w-3" /> {lead.phone}
            </a>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => copy(lead.email)} className="flex h-7 w-7 items-center justify-center rounded-md border border-brand-hair text-brand-mute hover:border-brand hover:text-brand" aria-label="Copy email"><Copy className="h-3 w-3" /></button>
        </div>
      </div>
      <div className="mt-3 rounded-md border border-brand-hair/60 bg-brand-panel2 p-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-mute">Last seen</div>
        <div className="mt-1 flex items-center gap-2 truncate text-[12.5px] text-brand-ivory">
          <ArrowUpRight className="h-3.5 w-3.5 flex-none text-brand" />
          <span className="truncate">{lead.last_path}</span>
          {lead.last_sub_code && <span className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-brand">{lead.last_sub_code}</span>}
        </div>
        <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-brand-mute">
          {lead.last_seen ? new Date(lead.last_seen).toLocaleString("en-IN") : ""}
        </div>
      </div>
      {lead.sub_codes && lead.sub_codes.length > 0 && (
        <div className="mt-3">
          <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.22em] text-brand-mute">Products viewed</div>
          <div className="flex flex-wrap gap-1.5">
            {lead.sub_codes.slice(0, 12).map((sc) => (
              <span key={sc} className="rounded bg-brand/10 px-2 py-0.5 font-mono text-[10.5px] uppercase tracking-[0.22em] text-brand">{sc}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
